import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { ConfirmDialog } from '../../common/views/modals/confirmDialog/ConfirmDialog';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { panelMessages } from '../messages/panelMessages';
import { recutErrorText } from '../timeline/recutErrorText';
import { useRecut } from '../timeline/useRecut';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * "Cut speed of the whole video" (design d3): the director reads it, so a new value recuts every
 * section. The slider asks first; "Keep the cut" puts it back.
 */
const CutSpeedSliderComponent: React.FC = () => {
  const intl = useIntl();
  const { doc, change, readOnly, mode, showUpsell } = useMusicVideoEditor();
  const stored = doc.look.cutSpeed;
  const [value, setValue] = useState(stored);
  const [isAsking, setIsAsking] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const { recut, isBusy, error, saveError } = useRecut();
  const errorText = recutErrorText(intl, error, saveError);
  useEffect(() => setValue(stored), [stored]);

  // The recut goes out after the editor rendered the new speed: the save before it
  // (`flushSave`) then holds the new speed, and the director reads the stored document.
  useEffect(() => {
    if (!isPending || Math.abs(stored - value) > 0.001) {
      return;
    }
    setIsPending(false);
    recut('all', [], '')
      .then((started) => {
        if (started) {
          setIsAsking(false);
        }
      })
      .catch(console.error);
  }, [isPending, recut, stored, value]);

  const commit = () => {
    if (Math.abs(value - stored) <= 0.001) {
      return;
    }
    // Showcase mode has no director: the new speed needs a recut, so the slider goes back and the
    // sign-up card opens. The cut does not change.
    if (mode === 'showcase') {
      setValue(stored);
      showUpsell('recut');
      return;
    }
    setIsAsking(true);
  };
  const confirm = () => {
    change((current) => ({ ...current, look: { ...current.look, cutSpeed: value } }));
    setIsPending(true);
  };

  return (
    <>
      <span className={styles.eyebrow}>{intl.formatMessage(panelMessages.cutSpeed)}</span>
      <div className={styles.sliderRow}>
        <span className={styles.hint}>{intl.formatMessage(panelMessages.calm)}</span>
        <input
          type="range"
          className={styles.range}
          min={0}
          max={1}
          step={0.05}
          value={value}
          disabled={readOnly}
          aria-label={intl.formatMessage(panelMessages.cutSpeed)}
          onChange={(event) => setValue(Number(event.target.value))}
          onPointerUp={commit}
          onKeyUp={commit}
        />
        <span className={styles.hint}>{intl.formatMessage(panelMessages.fast)}</span>
      </div>
      <p className={styles.hint}>{intl.formatMessage(panelMessages.cutSpeedHint)}</p>
      <ConfirmDialog
        isOpen={isAsking}
        tone="neutral"
        title={intl.formatMessage(panelMessages.cutSpeedConfirmTitle)}
        description={intl.formatMessage(panelMessages.cutSpeedConfirmText)}
        confirmLabel={intl.formatMessage(panelMessages.cutSpeedConfirm)}
        cancelLabel={intl.formatMessage(panelMessages.cutSpeedCancel)}
        isBusy={isBusy}
        error={errorText ?? undefined}
        onConfirm={confirm}
        onCancel={() => {
          setIsAsking(false);
          setValue(stored);
        }}
      />
    </>
  );
};

export const CutSpeedSlider = TypedMemo(CutSpeedSliderComponent);
